interface WidgetSectionProps {
  show?: boolean
  /** May be `null` because the underlying zod field is `.nullish()`. */
  title?: string | null
  className?: string
  /**
   * #1133: every section widget's `section.show` defaults to `false`, which
   * previously meant no heading element existed at all — a PDP with no
   * sections configured to show their label shipped zero H2s, so crawlers,
   * AI answer engines, and screen readers had no outline past the H1. When
   * `true` and `show` is false (title still present), render the H2 anyway,
   * visually hidden — present in markup without changing the page's visual
   * design. Defaults to `false` (unset) so every other WidgetSection caller
   * (gallery, reviews, rate-comparison, property-finder, explorer) stays
   * byte-identical; only the PDP section widgets opt in.
   */
  forceHeadingInMarkup?: boolean
}

/** Visually hidden but present for assistive tech / crawlers — same recipe as `.sr-only` in styles/common.css, inlined so it does not depend on that stylesheet being loaded into this widget's shadow DOM. */
const HIDDEN_HEADING_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

/**
 * Renders the styled section heading shown above a content widget on a PDP.
 * Visual: bold heading text on the left, horizontal divider line filling the
 * remaining width to the right. Returns null if `title` is empty (nothing to
 * head) or if `show` is false and `forceHeadingInMarkup` isn't set, so it
 * stays out of the layout entirely — the pre-#1133 behaviour.
 *
 * Style rules for the VISIBLE heading live in each widget's CSS file
 * (`.hr-widget-section*`).
 *
 * CONTRACT: section-nav derives its link labels from this markup — the
 * first `.hr-widget-section-title`'s textContent inside a section root
 * (widgets/section-nav/data.ts, deriveSectionLabel). Renaming that class or
 * nesting non-title text inside the visible h2 silently regresses those
 * labels. The `forceHeadingInMarkup` hidden heading below deliberately uses
 * a DIFFERENT class (`hr-widget-section-title-a11y`) so it is invisible to
 * that same-class query — section-nav's derived-label fallback (a widget
 * with its header hidden falls through to a toolkit default) stays exactly
 * as it is today.
 */
export default function WidgetSection({ show, title, className, forceHeadingInMarkup }: WidgetSectionProps) {
  if (!title) return null
  if (!show) {
    return forceHeadingInMarkup
      ? <h2 className="hr-widget-section-title-a11y" style={HIDDEN_HEADING_STYLE}>{title}</h2>
      : null
  }
  return (
    <div className={`hr-widget-section${className ? ` ${className}` : ''}`}>
      <h2 className="hr-widget-section-title">{title}</h2>
      <span className="hr-widget-section-line" aria-hidden="true" />
    </div>
  )
}
